import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ContractModel } from '../../../../../../core/models/contract.model';
import { ContractService } from '../../contract.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ProductModel } from '../../../../../../core/models/product.model';
import { PrestationModel } from '../../../../../../core/models/prestation.model';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { first } from 'rxjs/operators';
import { ContractTypeOfRevisionEnum } from '../../../../../../core/enums/contract.enum';
import { ProductService } from '../../../product/product.service';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { Sort } from '@angular/material/sort';
import { PrestationService } from '../../../prestation/prestation.service';
import { ContractStatusEnum } from '../../../../../../core/enums/contract-status.enum';
import { DEFAULT_PAGE_SIZE } from '../../../../../../core/config/base-table.config';
import { SupplierType } from '../../../../../../core/enums/supplier-type.enum';
import { ObjectApiInterface } from '../../../../../../core/base/interfaces/object-api.interface';
import { AuthService } from '../../../../../../core/services/auth.service';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';

@Component({
  selector: 'vex-contract-base',
  templateUrl: './contract-base.component.html',
  styleUrls: ['./contract-base.component.scss'],
})
export class ContractBaseComponent implements OnInit, OnDestroy {
  readonly ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  loading = false;
  subscription = new Subscription();
  tabs: Array<string> = ['Articles', 'Prestations'];
  entityType: 'products' | 'prestations';
  popupForm: UntypedFormGroup = new UntypedFormGroup({});
  contract: ContractModel;
  products: ProductModel[];
  prestations: PrestationModel[];
  isEditableCatalog = false;

  dataSourceArticle: MatTableDataSource<ProductModel>;
  dataSourcePrestation: MatTableDataSource<PrestationModel>;

  feisColumnsArticle: Array<FeiColumn> = [];
  feisColumnsPrestation: Array<FeiColumn> = [];

  displayedColumnsArticle = this.feisColumnsArticle.map((feiColumn: FeiColumn) => feiColumn.column);
  displayedColumnsPrestation = this.feisColumnsPrestation.map((feiColumn: FeiColumn) => feiColumn.column);

  countProduct = 0;
  countPrestation = 0;

  pageSize = DEFAULT_PAGE_SIZE;
  pageIndex: number;
  sortField: string;
  sortOrder: string;
  feisColumns: Array<FeiColumn>;

  hasArticle = false;
  prestationEmpty = false;
  hasPrestation = false;
  articleEmpty = false;

  constructor(
    public contractService: ContractService,
    public productService: ProductService,
    public prestationService: PrestationService,
    public authService?: AuthService,
  ) {}

  ngOnInit(): void {
    this.popupForm = new UntypedFormGroup({
      stockable: new UntypedFormControl(null),
      isSuspended: new UntypedFormControl(null),
      // Filtre « Visibilité eshop » du catalogue interne : le contrôle manquait,
      // la checkbox du template pointait dans le vide (PRED-3195)
      isAvailable: new UntypedFormControl(null),
    });
  }

  /**
   * PRED-3230 : les actions de ligne (« … ») d'un catalogue suivent les droits du module Engagement.
   * Modifier / suspendre exigent UPDATE_CONTRACT, archiver / supprimer exigent DELETE_CONTRACT ; les
   * règles métier déjà posées (statut, catalogue éditable) restent prioritaires.
   */
  applyContractPermissionsToActions(columns: Array<FeiColumn>): Array<FeiColumn> {
    const canUpdate = this.authService?.hasPermission(ENUM_PERMISSIONS.UPDATE_CONTRACT) ?? false;
    const canDelete = this.authService?.hasPermission(ENUM_PERMISSIONS.DELETE_CONTRACT) ?? false;
    return columns.map(column => {
      if (column.column === 'actions') {
        column.canUpdate = column.canUpdate && canUpdate;
        column.canSuspend = column.canSuspend && canUpdate;
        column.canArchive = column.canArchive && canDelete;
        column.canDelete = column.canDelete && canDelete;
      }
      return column;
    });
  }

  initData() {
    const sub1 = this.contractService.entity$.pipe().subscribe((contract: ContractModel) => {
      this.contract = contract;

      this.initDataSources();
    });

    const sub2 = this.contractService.entity$
      .pipe(
        first(), // Assure que l'observable émet une seule valeur
      )
      .subscribe((contract: ContractModel) => {
        this.contract = contract;

        this.initDataSources();

        this.manageTabs();

        this.updateColumnsBasedOnSustainabilityAndPrice();

        this.displayedColumnsArticle = this.feisColumnsArticle.map((feiColumn: FeiColumn) => feiColumn.column);
        this.displayedColumnsPrestation = this.feisColumnsPrestation.map((feiColumn: FeiColumn) => feiColumn.column);
      });

    this.subscription.add(sub1);
    this.subscription.add(sub2);
  }

  initDataSources(productFilters: any = {}) {
    this.loading = true;

    const itemsProduct = this.productService
      .findAll(this.pageIndex, this.pageSize, 'label', 'asc', null, {
        contracts: [this.contract._id],
        isAllEstablishment: true,
        // isWithExpired: true,
        requiredFields: ['*'],
        isInPurchaseFamily: true,
        aggregateCategory: true,
        ...productFilters,
      })
      .subscribe(res => {
        this.countProduct = res.totalData;
        this.products = res.data;
        this.dataSourceArticle = new MatTableDataSource<ProductModel>(res.data);
        this.loading = false;
      });

    const itemsPrestation = this.prestationService
      .findAll(this.pageIndex, this.pageSize, 'label', 'asc', null, {
        contractId: this.contract._id,
        isAllEstablishment: true,
        isPackageService: true,
        // isWithExpired: true,
        isWithRecurrence: true,
        isInPurchaseFamily: true,
        requiredFields: ['*'],
      })
      .subscribe(res => {
        this.countPrestation = res.totalData;
        this.prestations = res.data;
        this.dataSourcePrestation = new MatTableDataSource<PrestationModel>(res.data);
        this.loading = false;
      });

    this.subscription.add(itemsProduct);
    this.subscription.add(itemsPrestation);
  }

  manageTabs() {
    this.tabs = this.contractService.generateTabs(this.contract);
    this.hasArticle = this.tabs.includes('Articles');
    this.hasPrestation = this.tabs.includes('Prestations');
    this.tabChanges(this.hasArticle ? 0 : 1);
  }

  updateColumnsBasedOnSustainabilityAndPrice() {
    const { typeOfRevision } = this.contract.sustainabilityAndPriceTrans;

    if (typeOfRevision !== ContractTypeOfRevisionEnum.FIXED) {
      this.feisColumnsArticle = this.feisColumnsArticle.filter(
        (feiColumn: FeiColumn) => feiColumn.column !== 'revisionAmount',
      );
      this.feisColumnsPrestation = this.feisColumnsPrestation.filter(
        (feiColumn: FeiColumn) => feiColumn.column !== 'revisionAmount',
      );
    }
  }

  tabChanges(event: number): void {
    this.entityType = event === 1 ? 'prestations' : 'products';
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  pageEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.initDataSources();
  }

  sortEvent(event: Sort): void {
    const propertyDisplay = this.feisColumns.find(x => x.column === event.active);
    this.sortField = propertyDisplay ? propertyDisplay.propertyDisplay : event.active;
    this.sortField = propertyDisplay.propertyDisplay ? propertyDisplay.propertyDisplay : event.active;
    this.sortOrder = event.direction;
    this.initDataSources();
  }

  suspendItem(element: ProductModel | PrestationModel) {
    let call$: Observable<ObjectApiInterface<any>>;
    if (element.isSuspended) {
      call$ =
        this.entityType === 'products'
          ? this.productService.unsuspend(element as ProductModel)
          : this.prestationService.unsuspend(element as PrestationModel);
    } else {
      call$ =
        this.entityType === 'products'
          ? this.productService.suspend(element as ProductModel)
          : this.prestationService.suspend(element as PrestationModel);
    }

    call$.subscribe(() => this.initDataSources());
  }

  updateCanSuspendRule() {
    if (!this.authService) {
      return;
    }

    const currentUser = this.authService.currentUserValue();
    const isPredikUser = this.authService.isPredik();
    const isEngagementContact = currentUser?._id === this.contract?.engagementContact?._id;
    const canSuspend = isPredikUser || isEngagementContact;

    this.feisColumnsArticle = this.feisColumnsArticle.map(column => {
      if (column.column === 'actions') {
        column.canSuspend = canSuspend;
      }
      return column;
    });

    this.feisColumnsPrestation = this.feisColumnsPrestation.map(column => {
      if (column.column === 'actions') {
        column.canSuspend = canSuspend;
      }
      return column;
    });
  }
}
